import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import i18n from '../../i18n/i18n';
import {
  ClassroomAssignState,
  IGetAllTeachers,
  IGetByIdClassroomAssignResponse,
  IGetClassroomAssignResponse,
} from '../../interface/teacherClassroomAssignModel';
import {
  createClassroomAssign,
  deleteClassroomAssign,
  fetchAssignedTeacherReport,
  getAllTeachers,
  getClassroomAssign,
  getClassroomAssignById,
  updateClassroomAssign,
} from '../action/teacher-assign-classroom.action';
import { ICreateSubjectResponseBody } from '../../interface/subjectModel';

// --- State Interface ---

// --- Initial State ---
const initialState: ClassroomAssignState = {
  isLoading: false,
  error: null,
  success: false,
  assignList: null,
  assignDetails: null,
  teacherList: null,
  message: null,
};

// --- Slice ---
const classroomAssignSlice = createSlice({
  name: 'classroomAssign',
  initialState,
  reducers: {
    clearAssignState: (state) => {
      state.isLoading = false;
      state.error = null;
      state.success = false;
      state.message = null;
    },
  },
  extraReducers: (builder) => {
    // CREATE ASSIGN
    builder.addCase(createClassroomAssign.pending, (state) => {
      state.isLoading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(
      createClassroomAssign.fulfilled,
      (state, action: PayloadAction<ICreateSubjectResponseBody>) => {
        state.isLoading = false;
        state.success = true;
        state.message = action.payload.message || i18n.t('toast.classroom_assigned_successfully', 'Classroom assigned successfully');
      },
    );
    builder.addCase(createClassroomAssign.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload?.message || i18n.t('toast.failed_to_assign_classroom', 'Failed to assign classroom');
    });

    // UPDATE ASSIGN
    builder.addCase(updateClassroomAssign.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(
      updateClassroomAssign.fulfilled,
      (state, action: PayloadAction<ICreateSubjectResponseBody>) => {
        state.isLoading = false;
        state.success = true;
        state.message = action.payload.message || i18n.t('toast.classroom_assignment_updated', 'Classroom assignment updated');
      },
    );
    builder.addCase(updateClassroomAssign.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload?.message || i18n.t('toast.failed_to_update_classroom_assignment', 'Failed to update classroom assignment');
    });

    // DELETE ASSIGN
    builder.addCase(deleteClassroomAssign.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(
      deleteClassroomAssign.fulfilled,
      (state, action: PayloadAction<ICreateSubjectResponseBody>) => {
        state.isLoading = false;
        state.success = true;
        state.message = action.payload.message || i18n.t('toast.classroom_assignment_deleted', 'Classroom assignment deleted');
      },
    );
    builder.addCase(deleteClassroomAssign.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload?.message || i18n.t('toast.failed_to_delete_classroom_assignment', 'Failed to delete classroom assignment');
    });

    // GET ALL TEACHERS
    builder.addCase(getAllTeachers.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getAllTeachers.fulfilled, (state, action: PayloadAction<IGetAllTeachers>) => {
      state.isLoading = false;
      state.teacherList = action.payload;
    });
    builder.addCase(getAllTeachers.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload?.message || i18n.t('toast.failed_to_fetch_teacher_list', 'Failed to fetch teacher list');
    });

    // GET ALL ASSIGNED CLASSROOMS
    builder.addCase(getClassroomAssign.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(
      getClassroomAssign.fulfilled,
      (state, action: PayloadAction<IGetClassroomAssignResponse>) => {
        state.isLoading = false;
        state.assignList = action.payload;
      },
    );
    builder.addCase(getClassroomAssign.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload?.message || i18n.t('toast.failed_to_fetch_assigned_classrooms', 'Failed to fetch assigned classrooms');
    });

    // GET ASSIGN BY ID
    builder.addCase(getClassroomAssignById.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(
      getClassroomAssignById.fulfilled,
      (state, action: PayloadAction<IGetByIdClassroomAssignResponse>) => {
        state.isLoading = false;
        state.assignDetails = action.payload;
      },
    );
    builder.addCase(getClassroomAssignById.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload?.message || i18n.t('toast.failed_to_fetch_assignment_details', 'Failed to fetch assignment details');
    });

    // FETCH REPORT
    builder.addCase(fetchAssignedTeacherReport.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchAssignedTeacherReport.fulfilled, (state) => {
      state.isLoading = false;
      state.success = true;
      state.message = i18n.t('toast.teacher_report_downloaded_successfully', 'Teacher report downloaded successfully');
    });
    builder.addCase(fetchAssignedTeacherReport.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload?.message || i18n.t('toast.failed_to_download_teacher_report', 'Failed to download teacher report');
    });
  },
});

// --- Export Reducer & Actions ---
export const { clearAssignState } = classroomAssignSlice.actions;
export default classroomAssignSlice.reducer;
